import React, { useContext, useEffect, useState } from 'react';
import { useUser } from '../../globalState';
import { displayName, useView } from '../../util';
import { useTranslation } from 'react-i18next';
import useDocumentTitle from '../../useDocumentTitle';
import SidebarLayout from '../../SidebarLayout';
import CostCenterTreeEditor from '../../components/costcenter/CostCenterTreeEditor';
import SimpleCard from '../../components/SimpleCard';
import { Stack, ToggleButtonGroup } from '@mui/material';
import HeaderItem from '../../components/HeaderItem';
import ToggleButton from '../../components/ToggleButton';
import CostCenterDefinition from '../../components/costcenter/CostCenterDefinition';
import CostCenterAccounts from '../../components/costcenter/CostCenterAccounts';
import CostCenterSubscriptions from '../../components/costcenter/CostCenterSubscriptions';
import CostCenterUsers from '../../components/costcenter/CostCenterUsers';
import { CostCenterDTO } from '../../dto';
import { useCostCentersLoadable } from 'globalState/costcenter';
import { useUnsetLoadingTabState } from '../../globalState/loading';
import Spinner from '../../components/Spinner';

interface CostCenterContextProps {
  selectedCostCenter: CostCenterDTO;
  setSelectedCostCenter: (costCenter: CostCenterDTO) => void;
}

const CostCenterContext = React.createContext<CostCenterContextProps>({} as never);

export const useCostCenterContext = () => useContext(CostCenterContext);

const CostCenters: React.FC = () => {
  const rootCostCenterLoadable = useCostCentersLoadable();
  const unsetLoadingTabState = useUnsetLoadingTabState();

  useEffect(() => {
    if (rootCostCenterLoadable?.state !== 'loading') {
      unsetLoadingTabState();
    }
  }, [rootCostCenterLoadable?.state, unsetLoadingTabState]);

  if (rootCostCenterLoadable?.state === 'loading') {
    return <Spinner />;
  } else {
    return <CostCentersContent costCenter={rootCostCenterLoadable?.contents.data} />;
  }
};

displayName(CostCenters, 'CostCenters');

export default CostCenters;

interface Props {
  costCenter: CostCenterDTO;
}

export const CostCentersContent: React.FC<Props> = ({ costCenter }) => {
  const user = useUser();
  const view = useView();
  const { t } = useTranslation();
  const [selectedCostCenter, setSelectedCostCenter] = useState(costCenter);
  const [costCenterView, setCostCenterView] = useState('definition');

  useDocumentTitle(t('application.shortName') + ' - ' + view.label);
  const handleViewChange = (_: unknown, view: string) => {
    if (!!view) {
      setCostCenterView(view);
    }
  };

  return (
    <CostCenterContext.Provider value={{ selectedCostCenter, setSelectedCostCenter }}>
      <SidebarLayout title={view.label} sidebarContent={<CostCenterTreeEditor showNonVirtualOnly={true} />}>
        <SimpleCard>
          <Stack direction="row" alignItems="center" height={theme => theme.spacing(7)} gap={7} paddingX={4}>
            <HeaderItem label={t('costCenters.header.label')} value={selectedCostCenter.name} />
            <HeaderItem label={t('costCenters.header.toggle.user')} value={`${user.firstName} ${user.lastName}`} />
            <ToggleButtonGroup color="primary" exclusive={true} value={costCenterView} onChange={handleViewChange}>
              <ToggleButton value="definition">{t('costCenters.header.toggle.definition')}</ToggleButton>
              <ToggleButton value="accounts">{t('costCenters.header.toggle.accounts')}</ToggleButton>
              <ToggleButton value="subscriptions">{t('costCenters.header.toggle.subscriptions')}</ToggleButton>
              <ToggleButton value="users">{t('costCenters.header.toggle.users')}</ToggleButton>
            </ToggleButtonGroup>
          </Stack>
        </SimpleCard>
        <Stack marginTop={4}>
          {costCenterView === 'definition' && <CostCenterDefinition />}
          {costCenterView === 'accounts' && <CostCenterAccounts />}
          {costCenterView === 'subscriptions' && <CostCenterSubscriptions rootCostCenter={selectedCostCenter} />}
          {costCenterView === 'users' && <CostCenterUsers />}
        </Stack>
      </SidebarLayout>
    </CostCenterContext.Provider>
  );
};

displayName(CostCentersContent, 'CostCentersContent');
